import { useTrackingSlice } from '@frontend/redux';
import { TRACKING_PARAMS } from '@frontend/redux/slices/trackingSlice';
import { useAnalytics } from '@frontend/web/hooks/useAnalytics';
import type { AuthContext } from '@frontend/web/hooks/useAuthentication';
import { createRootRouteWithContext, Outlet, useLocation, useScrollRestoration } from '@tanstack/react-router';
import { useEffect } from 'react';

export type RedirectContext = {
  isClientLoading: boolean;
  isClientStatusLoading: boolean;
  isGrowthbookReady: boolean;
  isSignupChangesEnabled: boolean;
  membershipComplete: boolean;
  onboardingComplete: boolean;
  profileComplete: boolean;
};

export type RouterContext = {
  authentication: MaybeUndefined<AuthContext>;
  redirectContext: MaybeUndefined<RedirectContext>;
};

const Root = () => {
  useScrollRestoration();

  const location = useLocation();
  const { trackRouteChange } = useAnalytics();
  const { setFbclid, setGclid, setImRef, setIrClickId, setRdtClickId, setTwClickId } = useTrackingSlice();

  useEffect(() => {
    trackRouteChange({
      properties: {
        path: location.pathname,
        search: location.search as Record<string, string>,
        searchStr: location.searchStr,
        title: window.document.title,
        url: `${window.location.origin}${location.href}`,
      },
    });

    const searchParams = new URLSearchParams(window.location.search);
    const irClickId = searchParams.get(TRACKING_PARAMS.IRCLICKID);
    const imRef = searchParams.get(TRACKING_PARAMS.IM_REF);
    const rdtCid = searchParams.get(TRACKING_PARAMS.RDT_CID);
    const twclid = searchParams.get(TRACKING_PARAMS.TWCLID);
    const gclid = searchParams.get(TRACKING_PARAMS.GCLID);
    const fbclid = searchParams.get(TRACKING_PARAMS.FBCLID);

    if (irClickId) {
      setIrClickId(irClickId);
    }
    if (imRef) {
      setImRef(imRef);
    }
    if (rdtCid) {
      setRdtClickId(rdtCid);
    }
    if (twclid) {
      setTwClickId(twclid);
    }
    if (gclid) {
      setGclid(gclid);
    }
    if (fbclid) {
      setFbclid(fbclid);
    }
  }, [location, setFbclid, setGclid, setImRef, setIrClickId, setRdtClickId, setTwClickId, trackRouteChange]);

  return <Outlet />;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
});
